import React from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useProjectDetail } from "@/hooks/useProjectDetail";
import { useProjectTransactions } from "@/hooks/useProjectTransactions";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFilters } from "@/contexts/FiltersContext";
import ProjectMap from "@/components/ProjectMap";
import TransactionList from "@/components/TransactionList";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Award,
  Clock,
  TreePine,
  Tag,
  BarChart,
  FileText,
  Download,
  Receipt,
  Image,
} from "lucide-react";
import { toast } from "sonner";
import { fetchProjectDocument } from "@/services/api";
import { getVerificationStandardName } from "@/lib/verification-standards";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, getCountryName } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CroppedId from "@/components/CroppedId";
import { useState } from "react";
import ProjectMediaThumbnailSlider from "@/components/ProjectMediaThumbnailSlider";
import ProjectMediaFullScreenSlider from "@/components/ProjectMediaFullScreenSlider";
import formatCO2eCredits from "@/utils/formatAmountToCO2Credits";
import getProjectTranslation from "@/utils/getProjectTranslation";

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { project, projectMedia, isLoading, isError } = useProjectDetail(
    projectId || "",
  );
  const { t, getLocalizedPath, language } = useLanguage();
  const { filters } = useFilters();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const {
    transactions,
    isLoading: isLoadingTransactions,
    meta: transactionsMeta,
    pagination,
    goToPage,
  } = useProjectTransactions(projectId || "");

  React.useEffect(() => {
    if (isError) {
      toast.error(t("common.error"), {
        description: t("projectDetail.projectNotFoundDesc"),
      });
    }
  }, [isError, t]);

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return t("projectDetail.certificationInProgress"); // Use translation for flexibility
    }
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const convertToHectares = (squareMeters?: number) => {
    if (!squareMeters) return "N/A";
    const hectares = squareMeters / 10000;
    return formatNumber(hectares, language) + " ha";
  };

  const getCountryFlag = (countryCode?: string) => {
    if (!countryCode) return "🌎";

    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));

    return String.fromCodePoint(...codePoints);
  };

  const handleDocumentDownload = async (
    documentId: string,
    documentTitle: string,
  ) => {
    if (!projectId) return;

    try {
      toast.loading(
        t("projectDetail.preparingDownload").replace(
          "{documentTitle}",
          documentTitle,
        ),
      );

      const documentData = await fetchProjectDocument(projectId, documentId);

      if (!documentData.url) {
        toast.error(
          t("projectDetail.downloadFailed").replace(
            "{documentTitle}",
            documentTitle,
          ),
        );
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = documentData.url;
      anchor.download = documentTitle;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      toast.dismiss();
      toast.success(
        t("projectDetail.downloading").replace(
          "{documentTitle}",
          documentTitle,
        ),
      );
    } catch (error) {
      toast.dismiss();
      console.error("Error downloading document:", error);
      toast.error(
        t("projectDetail.downloadFailed").replace(
          "{documentTitle}",
          documentTitle,
        ),
      );
    }
  };

  const buildFiltersQuery = () => {
    const params = new URLSearchParams(filters);
    return params.toString();
  };

  if (isLoading) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-20">
          <p className="text-gray-500">{t("projectDetail.loadingProject")}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <h1 className="text-2xl font-medium mb-4">
            {t("projectDetail.projectNotFound")}
          </h1>
          <p className="text-gray-500 mb-6">
            {t("projectDetail.projectNotFoundDesc")}
          </p>
          <Link
            to={`${getLocalizedPath("/projects")}?${searchParams.toString()}`}
          >
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("projectDetail.backToProjects")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasDocuments =
    project.projectDocuments && project.projectDocuments.length > 0;

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <Link
            to={`${getLocalizedPath("/projects")}?${buildFiltersQuery()}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>{t("projectDetail.backToProjects")}</span>
          </Link>
          <h1 className="text-2xl md:text-3xl font-medium">
            {getProjectTranslation(project.nameTranslations, language) ||
              project.title}
          </h1>
        </div>

        {project.projectType && (
          <Badge className="self-start md:self-auto bg-blue-100 text-blue-800 hover:bg-blue-200 border-0 px-3 py-1 mt-2 md:mt-0">
            <Tag className="h-3.5 w-3.5 mr-1" />
            {project.projectType.title}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-medium mb-4">
              {t("projectDetail.projectDetails")}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="flex items-start">
                <Clock className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">
                    {t("projectDetail.projectPeriod")}
                  </p>
                  <p className="font-medium">
                    {formatDate(project.start)} - {formatDate(project.end)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <TreePine className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">
                    {t("projectDetail.area")}
                  </p>
                  <p className="font-medium">
                    {convertToHectares(project.area)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Award className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">
                    {t("projectDetail.certificationDate")}
                  </p>
                  <p className="font-medium">
                    {formatDate(project.certificationDate)}
                  </p>
                </div>
              </div>

              {project.riskBuffer && (
                <div className="flex items-start">
                  <BarChart className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">
                      {t("projectDetail.riskBuffer")}
                    </p>
                    <p className="font-medium">{project.riskBuffer}%</p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-5">
              <h3 className="font-medium mb-2">
                {t("projectDetail.creditAvailability")}
              </h3>
              <Badge
                className={`${
                  project.creditAvailability === "credits_available"
                    ? "bg-green-100 text-green-800 hover:bg-green-200"
                    : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                } border-0 px-3 py-1`}
              >
                {project.creditAvailability === "credits_available"
                  ? t("projectDetail.creditsAvailable")
                  : t("projectDetail.noCreditsAvailable")}
              </Badge>
            </div>

            {project.description && (
              <div className="border-t border-gray-100 pt-5 mt-5">
                <h3 className="font-medium mb-2">
                  {t("projectDetail.description")}
                </h3>
                <p className="text-gray-700">{project.description}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-0">
              <ProjectMap
                project={project}
                className="h-[300px] w-full"
                showRecenterButton={true}
              />
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <span
                  className="text-xl mr-1"
                  title={project.countryCode?.toUpperCase()}
                >
                  {getCountryFlag(project.countryCode)}
                </span>
                <span>
                  {project.countryCode && (
                    <span className="font-medium mr-1">
                      {getCountryName(project.countryCode)}
                    </span>
                  )}
                  {project.countryCode && project.location && (
                    <span>- {project.location}</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {project.batches && project.batches.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-medium mb-4">
                {t("projectDetail.projectBatches")}
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-sm text-gray-600">
                        {t("projectDetail.batch")}
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-gray-600">
                        {t("projectDetail.period")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.batches.map((batch) => (
                      <tr key={batch.id} className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">{batch.title}</td>
                        <td className="py-3 px-4 text-gray-700">
                          {formatDate(batch.from)} - {formatDate(batch.to)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Project Media Section */}
          {projectMedia && projectMedia.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-medium mb-4 flex items-center">
                <Image className="w-5 h-5 mr-2 text-gray-700" />
                {t("projectDetail.media")}
              </h2>
              <ProjectMediaThumbnailSlider
                projectId={projectId || ""}
                media={projectMedia}
                onImageClick={(index) => setSelectedImageIndex(index)}
              />
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-medium mb-4">
              {t("projectDetail.participants")}
            </h2>

            {project.projectDeveloper && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  {t("projectDetail.projectDeveloper")}
                </p>
                <p className="font-medium">{project.projectDeveloper.name}</p>
              </div>
            )}

            {project.defaultIssuer && (
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {t("projectDetail.defaultIssuer")}
                </p>
                <div className="flex items-center">
                  {project.defaultIssuer.logoUrl && (
                    <img
                      src={project.defaultIssuer.logoUrl}
                      alt={project.defaultIssuer.name}
                      className="h-6 mr-2"
                    />
                  )}
                  <p className="font-medium">{project.defaultIssuer.name}</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-medium mb-4">
              {t("projectDetail.projectInformation")}
            </h2>

            <div className="space-y-3">
              <div className="flex items-center">
                <CroppedId
                  id={project.id}
                  size="md"
                  label={t("projectDetail.id")}
                  showCopyButton={true}
                />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t("projectDetail.created")}
                </p>
                <p className="font-medium">{formatDate(project.createdAt)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t("projectDetail.lastUpdated")}
                </p>
                <p className="font-medium">{formatDate(project.updatedAt)}</p>
              </div>

              {!!project.forecastedAmountYearly && (
                <div>
                  <p className="text-sm text-gray-600">
                    {t("projectDetail.forecastedAmount")}
                  </p>
                  <p className="font-medium">
                    {formatCO2eCredits(
                      project.forecastedAmountYearly,
                      t,
                      language,
                    )}
                  </p>
                </div>
              )}

              {project.verificationStandard && (
                <div>
                  <p className="text-sm text-gray-600">
                    {t("projectDetail.verificationStandard")}
                  </p>
                  <p className="font-medium">
                    {getVerificationStandardName(
                      project.verificationStandard.id,
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 mb-8">
        <div className="p-6">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="w-full mb-6 grid grid-cols-2">
              <TabsTrigger
                value="transactions"
                className="text-base flex items-center gap-2"
              >
                <Receipt className="h-5 w-5" />
                <span>{t("projectDetail.transactions")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="documents"
                className="text-base flex items-center gap-2"
              >
                <FileText className="h-5 w-5" />
                <span>{t("projectDetail.documents")}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="transactions"
              className="mt-0 pt-2 focus-visible:outline-none focus-visible:ring-0"
            >
              <TransactionList
                transactions={transactions}
                isLoading={isLoadingTransactions}
                meta={transactionsMeta}
                onPageChange={goToPage}
                currentPage={pagination.page}
              />

              {transactions.length > 0 && (
                <div className="mt-4 text-center">
                  <Link
                    to={getLocalizedPath(
                      `/transactions?projectId=${projectId}`,
                    )}
                  >
                    <Button variant="outline">
                      {t("projectDetail.viewAllTransactions")}
                    </Button>
                  </Link>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="documents"
              className="mt-0 pt-2 focus-visible:outline-none focus-visible:ring-0"
            >
              {hasDocuments ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("projectDetail.document")}</TableHead>
                      <TableHead>{t("projectDetail.date")}</TableHead>
                      <TableHead className="text-right">
                        {t("projectDetail.action")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {project.projectDocuments &&
                      project.projectDocuments.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center">
                              <FileText className="h-4 w-4 text-gray-500 mr-2" />
                              <span>{doc.title}</span>
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(doc.updatedAt)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDocumentDownload(doc.id, doc.title)
                              }
                            >
                              <Download className="h-4 w-4 mr-1" />
                              {t("projectDetail.download")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    {t("projectDetail.noDocuments")}
                  </h3>
                  <p className="text-gray-500">
                    {t("projectDetail.noDocumentsDesc")}
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Full Screen Image Slider */}
      {selectedImageIndex !== null &&
        projectMedia &&
        projectMedia.length > 0 && (
          <ProjectMediaFullScreenSlider
            projectId={projectId || ""}
            media={projectMedia}
            initialIndex={selectedImageIndex}
            onClose={() => setSelectedImageIndex(null)}
          />
        )}
    </div>
  );
};

export default ProjectDetail;
